import {
  RedoDiscountApplication,
  RedoDiscountApplicationAllocationMethod,
  RedoDiscountApplicationTargetSelection,
  RedoDiscountApplicationTargetType,
  RedoDiscountApplicationType,
} from "@redotech/redo-customer-event-common/events/storefront-event-primitives";
import { sharedEmailSchemaFields } from "@redotech/redo-flows-core-schema-common/shared-email-schema-fields";
import { AdvancedFlowTokenType } from "@redotech/redo-flows-core-schema-common/type-system/data-types/base-types";
import {
  defineSchema,
  SchemaInstance,
} from "@redotech/redo-flows-core-schema-common/type-system/schema";
import {
  MarketingEntryPoint,
  marketingEntryPointPrettyNames,
} from "@redotech/redo-marketing-entry-point-common/marketing-entry-point";
import { SubscriptionIntegrationType } from "@redotech/redo-subscriptions-common/subscription-integration";
import { CurrencyCode } from "@redotech/shopify-client/storefront.graphql";
import { getPlainDateFromDateUtc } from "@redotech/util/date";
import {
  branchableCartContextFields,
  oneClickUnsubscribeLinkFields,
  recommendationCartContextFields,
  redoCustomerIdFields,
  unsubscribeLinkFields,
} from "../common-fields";
import { subscriptionUpsellSchemaFields } from "../subscription-upsell-fields";
import {
  browsedProductsFields,
  cartProductsFields,
  discountedProductFields,
  exampleTriggerProduct,
  exampleTriggerProducts,
  lowStockProductFields,
  restockedProductFields,
} from "./product-variables";

const discountCodeSchema = defineSchema({
  fields: {
    discountCode: {
      dataType: "Maybe Text",
      documentation: "The discount code to share with the customer.",
      hideFromConditionStep: true,
    },
  },
});

export const baseMarketingSchemaWithoutInteractiveCart = defineSchema({
  fields: {
    ...redoCustomerIdFields,
    customerFirstName: {
      dataType: "Maybe Text",
      documentation:
        "The first name of the customer who signed up for marketing messages.",
      hideFromConditionStep: true,
    },
    customerLastName: {
      dataType: "Maybe Text",
      documentation:
        "The last name of the customer who signed up for marketing messages.",
      hideFromConditionStep: true,
    },
    customerFullName: {
      dataType: "Maybe Text",
      documentation:
        "The full name of the customer who signed up for marketing messages.",
      hideFromConditionStep: true,
    },
  },
});

export const baseMarketingSchema = defineSchema({
  fields: {
    ...baseMarketingSchemaWithoutInteractiveCart.fields,
    // Abandonment schemas re-declare this field branchable, which is what
    // offers cart branching in the editor.
    ...recommendationCartContextFields(
      "Data that is useful for generating product recommendations for an interactive cart.",
    ),
  },
});

export const baseMarketingEmailSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    customerEmail: {
      dataType: "Email",
      documentation: "The email address of the customer.",
    },
    ...unsubscribeLinkFields,
    ...oneClickUnsubscribeLinkFields,
    ...sharedEmailSchemaFields,
  },
});

export const baseMarketingBackInStockSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    productVariantId: {
      dataType: "Text",
      documentation: "The product variant ID that is out of stock.",
    },
    backInStockProductUrl: {
      dataType: "Maybe Url",
      documentation: "A link to the product's page on your store.",
    },
    backInStockProductTitle: {
      dataType: "Maybe Text",
      documentation: "The title of the product that is back in stock.",
    },
    ...unsubscribeLinkFields,
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email address of the customer.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer.",
    },
    oldInventoryCount: {
      dataType: "Maybe Number",
      documentation: "The inventory count for the product variant pre-restock.",
    },
    newInventoryCount: {
      dataType: "Maybe Number",
      documentation:
        "The inventory count for the product variant post-restock.",
    },
    ...restockedProductFields,
  },
});

export const baseMarketingLowInventorySchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    productVariantId: {
      dataType: "Text",
      documentation: "The product variant ID that is out of stock.",
    },
    productTitle: {
      dataType: "Maybe Text",
      documentation: "The title of the product that is low in stock.",
    },
    variantTitle: {
      dataType: "Maybe Text",
      documentation: "The title of the variant that is low in stock.",
    },
    inventoryCount: {
      dataType: "Maybe Number",
      documentation:
        "The total sellable quantity of the variant across all locations.",
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email address of the customer.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer.",
    },
    ...lowStockProductFields,
  },
});

export const baseMarketingSignupSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    alreadySubscribed: {
      dataType: "Boolean",
      documentation:
        "Whether the customer was already subscribed to marketing messages.",
    },
    entryPoint: {
      dataType: "Enum",
      values: Object.values(MarketingEntryPoint).filter(
        (value) =>
          // These entry points don't trigger flows
          ![MarketingEntryPoint.MERCHANT, MarketingEntryPoint.IMPORT].includes(
            value,
          ),
      ) as MarketingEntryPoint[],
      documentation: "Where the customer signed up for marketing messages.",
      prettyValues: marketingEntryPointPrettyNames,
    },
    form: {
      dataType: "Maybe Token",
      tokenType: AdvancedFlowTokenType.SIGNUP_FORM_ID,
      documentation: "The form that the customer signed up through, if any.",
    },
    /** @deprecated Superseded by `form`, which picks a form instead of typing an id. */
    formId: {
      dataType: "Maybe Text",
      documentation: "The form that the customer signed up through, if any.",
      hideFromConditionStep: true,
    },
    customerEventId: {
      dataType: "Maybe Text",
      documentation: "The ID of the customer event that triggered the signup.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
  },
});

export const customerGroupMembershipChangeSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    customerEmail: {
      dataType: "Maybe Email",
      documentation:
        "The email address of the customer who abandoned the cart.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer who abandoned the cart.",
    },
    ...unsubscribeLinkFields,
    ...oneClickUnsubscribeLinkFields,
    segment: {
      dataType: "Token",
      tokenType: AdvancedFlowTokenType.CUSTOMER_GROUP_ID,
      documentation: "The segment that the customer entered or exited.",
    },
  },
});

export const emailMarketingSignupSchema = defineSchema({
  fields: {
    ...baseMarketingSignupSchema.fields,
    ...discountCodeSchema.fields,
    ...unsubscribeLinkFields,
    ...oneClickUnsubscribeLinkFields,
    customerEmail: {
      dataType: "Email",
      documentation:
        "The email address of the customer who signed up for marketing emails.",
    },
  },
});

const baseAbandonmentSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    isAbandonment: {
      dataType: "Boolean",
      documentation: "A flag to indicate that this is an abandonment.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    ...unsubscribeLinkFields,
    ...oneClickUnsubscribeLinkFields,
    customerEventId: {
      dataType: "Maybe Text",
      documentation:
        "The ID of the customer event that triggered the abandonment.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
  },
});

export const baseMarketingCartAbandonmentSchema = defineSchema({
  fields: {
    ...baseAbandonmentSchema.fields,
    isAbandonment: {
      dataType: "Boolean",
      documentation: "A flag to indicate that this is an abandonment.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    time: {
      dataType: "DateTime",
      documentation: "The time the last item was added to the cart.",
    },
    isCartAbandoned: {
      dataType: "Boolean",
      label: "Cart abandoned",
      documentation:
        "Whether the cart is still abandoned. Becomes false once the customer starts checkout or orders.",
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation:
        "The email address of the customer who abandoned the cart.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer who abandoned the cart.",
    },
    storeUrl: {
      dataType: "Maybe Url",
      documentation: "The web address of your store.",
    },
    checkoutUrl: {
      dataType: "Maybe Url",
      documentation: "The URL of the checkout that the customer abandoned.",
    },
    productInCartName: {
      dataType: "Maybe Text",
      documentation: "The name of the first product in the abandoned cart.",
    },
    ...branchableCartContextFields(
      "Branch on what is in the abandoned cart: its products, collections, tags, count, or discount eligibility.",
    ),
    ...cartProductsFields,
  },
});

export const baseMarketingBrowseAbandonmentSchema = defineSchema({
  fields: {
    ...baseAbandonmentSchema.fields,
    isAbandonment: {
      dataType: "Boolean",
      documentation: "A flag to indicate that this is an abandonment.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    time: {
      dataType: "DateTime",
      documentation: "When the browsing last occurred.",
    },
    isBrowseAbandoned: {
      dataType: "Boolean",
      label: "Browse abandoned",
      documentation:
        "Whether the browsing is still abandoned. Becomes false once the customer adds to cart or orders.",
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation:
        "The email address of the customer who abandoned their browsing session.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation:
        "The phone number of the customer who abandoned their browsing session.",
    },
    browsedPageUrl: {
      dataType: "Maybe Url",
      documentation: "The URL of the page the customer was browsing.",
    },
    ...browsedProductsFields,
  },
});

const dummyCartContext = (generatedByRedo: boolean) => ({
  existingCartId: generatedByRedo ? "preview" : null,
  productIdsForRecommendations: [],
  productIdsAlreadyInCart: [],
  alreadyInCart: [],
});

const exampleDiscountApplications: RedoDiscountApplication[] = [
  {
    title: "SUMMER20",
    type: RedoDiscountApplicationType.DISCOUNT_CODE,
    allocationMethod: RedoDiscountApplicationAllocationMethod.ACROSS,
    targetSelection: RedoDiscountApplicationTargetSelection.ALL,
    targetType: RedoDiscountApplicationTargetType.LINE_ITEM,
    value: { type: "percentage", percentage: 20 },
  },
  {
    title: "Auto $10 Off",
    type: RedoDiscountApplicationType.AUTOMATIC,
    allocationMethod: RedoDiscountApplicationAllocationMethod.ACROSS,
    targetSelection: RedoDiscountApplicationTargetSelection.ALL,
    targetType: RedoDiscountApplicationTargetType.LINE_ITEM,
    value: { type: "money", amount: 10, currencyCode: "USD" },
  },
];

const exampleBaseMarketingSchemaInstance: SchemaInstance<
  typeof baseMarketingSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  customerFirstName: "John",
  customerLastName: "Doe",
  customerFullName: "John Doe",
  cartContext: dummyCartContext(true),
};

export const baseMarketingCheckoutAbandonmentSchema = defineSchema({
  fields: {
    ...baseAbandonmentSchema.fields,
    isAbandonment: {
      dataType: "Boolean",
      documentation: "A flag to indicate that this is an abandonment.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    time: {
      dataType: "DateTime",
      documentation: "When the last customer action on checkout occurred.",
    },
    isCheckoutAbandoned: {
      dataType: "Boolean",
      label: "Checkout abandoned",
      documentation:
        "Whether the checkout is still abandoned. Becomes false once the customer places an order.",
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation:
        "The email address of the customer who abandoned their browsing session.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation:
        "The phone number of the customer who abandoned their browsing session.",
    },
    checkout: {
      dataType: "Maybe Shopify Checkout",
      documentation: "Data about the customer's checkout session.",
      hideFromConditionStep: true,
    },
    // TODO: booka66 - Remove this legacy cartSubtotal field once all legacy cart subtotal automations are migrated to use the cart condition
    cartSubtotal: {
      dataType: "Maybe Number",
      documentation:
        "The subtotal amount of the abandoned cart (in the store's currency).",
    },
    cartProductCount: {
      dataType: "Maybe Number",
      documentation: "The number of unique products in the abandoned cart.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    ...branchableCartContextFields(
      "Branch on what is in the abandoned cart: its products, collections, tags, count, or subtotal.",
    ),
    discountApplications: {
      dataType: "Maybe Discount Applications",
      documentation:
        "Discount codes and automatic discounts applied to the checkout.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    cartPreDiscountTotal: {
      dataType: "Maybe Number",
      documentation: "The cart total before any discounts were applied.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    checkoutNoteAttributeTokens: {
      dataType: "Multiple Text",
      documentation:
        "Note attributes from the checkout, listed as `key` and `key=value`.",
      hideFromInsertionModal: true,
      hideFromConditionStep: false,
    },
    checkoutLineItemPropertyTokens: {
      dataType: "Multiple Text",
      documentation:
        "Line item properties from the checkout, listed as `key` and `key=value`.",
      hideFromInsertionModal: true,
      hideFromConditionStep: false,
    },
    hasInferredBundle: {
      dataType: "Maybe Boolean",
      documentation:
        "Whether the cart was flagged as containing a custom bundle.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    storeUrl: {
      dataType: "Maybe Url",
      documentation: "The web address of your store.",
    },
    checkoutUrl: {
      dataType: "Maybe Url",
      documentation: "The URL of the checkout that the customer abandoned.",
    },
    productInCartName: {
      dataType: "Maybe Text",
      documentation: "The name of the first product in the abandoned cart.",
    },
    sourceName: {
      dataType: "Maybe Text",
      documentation:
        "The source of the checkout: web, pos, shopify_draft_order, etc.",
      hideFromInsertionModal: true,
    },
    ...cartProductsFields,
    ...subscriptionUpsellSchemaFields,
  },
});

const exampleAbandonmentSchemaInstance: SchemaInstance<
  typeof baseAbandonmentSchema
> = {
  ...exampleBaseMarketingSchemaInstance,
  isAbandonment: true,
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  customerEventId: "67f8d6d38fd4c4b4d1c19876",
};

export const exampleBaseMarketingCheckoutAbandonmentSchemaInstance: SchemaInstance<
  typeof baseMarketingCheckoutAbandonmentSchema
> = {
  ...exampleAbandonmentSchemaInstance,
  isAbandonment: true,
  time: Temporal.Now.instant(),
  isCheckoutAbandoned: true,
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  checkout: {
    attributes: [],
    billingAddress: null,
    buyerAcceptsEmailMarketing: false,
    buyerAcceptsSmsMarketing: false,
    currencyCode: CurrencyCode.Usd,
    delivery: null,
    discountApplications: exampleDiscountApplications,
    discountsAmount: null,
    email: "john.doe@example.com",
    lineItems: [],
    localization: {
      country: { isoCode: null },
      language: { isoCode: "en" },
      market: { handle: null, id: null },
    },
    order: null,
    shippingAddress: null,
    shippingLine: null,
    smsMarketingPhone: null,
    subtotalPrice: { amount: 100, currencyCode: CurrencyCode.Usd },
    phone: "+1234567890",
    token: "",
    totalPrice: { amount: 105, currencyCode: CurrencyCode.Usd },
    totalTax: { amount: 5, currencyCode: CurrencyCode.Usd },
    transactions: [],
  },
  cartSubtotal: 100,
  cartProductCount: 3,
  cartContext: dummyCartContext(false),
  storeUrl: "https://example.com",
  checkoutUrl: "https://example.com/checkout",
  productInCartName: "Example Product",
  subscribableProductInCartName: "Subscribable Product",
  hasSubscribableProduct: true,
  subscriptionCheckoutUrl: "https://example.com/checkout/c/abcdef",
  subscribableLines: [
    {
      variantGid: "gid://shopify/ProductVariant/123",
      sellingPlanGid: "gid://shopify/SellingPlan/456",
      quantity: 1,
    },
  ],
  sourceName: "web",
  discountApplications: exampleDiscountApplications,
  cartPreDiscountTotal: 125,
  checkoutNoteAttributeTokens: ["consultantId", "consultantId=US-81000104239"],
  checkoutLineItemPropertyTokens: [
    "_consultantId",
    "_consultantId=US-81000104239",
  ],
  hasInferredBundle: false,
  cartProducts: exampleTriggerProducts,
};

export const exampleBaseMarketingBrowseAbandonmentSchemaInstance: SchemaInstance<
  typeof baseMarketingBrowseAbandonmentSchema
> = {
  ...exampleAbandonmentSchemaInstance,
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  isBrowseAbandoned: true,
  isAbandonment: true,
  time: Temporal.Now.instant(),
  cartContext: dummyCartContext(false),
  browsedPageUrl: "https://example.com/collections/shoes",
  browsedProducts: exampleTriggerProducts,
};

export const exampleBaseMarketingCartAbandonmentSchemaInstance: SchemaInstance<
  typeof baseMarketingCartAbandonmentSchema
> = {
  ...exampleAbandonmentSchemaInstance,
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  isCartAbandoned: true,
  isAbandonment: true,
  time: Temporal.Now.instant(),
  cartContext: dummyCartContext(false),
  storeUrl: "https://example.com",
  checkoutUrl: "https://example.com/checkout",
  productInCartName: "Example Product",
  cartProducts: exampleTriggerProducts,
};

export const baseMarketingNonShopifyCartAbandonmentSchema = defineSchema({
  fields: {
    ...baseAbandonmentSchema.fields,
    isAbandonment: {
      dataType: "Boolean",
      documentation: "A flag to indicate that this is an abandonment.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    time: {
      dataType: "DateTime",
      documentation: "The time the last item was added to the cart.",
    },
    isCartAbandoned: {
      dataType: "Boolean",
      label: "Cart abandoned",
      documentation:
        "Whether the cart is still abandoned. Becomes false once the customer starts checkout or orders.",
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation:
        "The email address of the customer who abandoned the cart.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer who abandoned the cart.",
    },
    storeUrl: {
      dataType: "Maybe Url",
      documentation: "The web address of your store.",
    },
    checkoutUrl: {
      dataType: "Maybe Url",
      documentation: "The URL of the checkout that the customer abandoned.",
    },
    productInCartName: {
      dataType: "Maybe Text",
      documentation: "The name of the first product in the abandoned cart.",
    },
    ...cartProductsFields,
  },
});

export const baseMarketingCsBrowseAbandonmentSchema = defineSchema({
  fields: {
    ...baseAbandonmentSchema.fields,
    isAbandonment: {
      dataType: "Boolean",
      documentation: "A flag to indicate that this is an abandonment.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    time: {
      dataType: "DateTime",
      documentation: "When the browsing last occurred.",
    },
    isBrowseAbandoned: {
      dataType: "Boolean",
      label: "Browse abandoned",
      documentation:
        "Whether the browsing is still abandoned. Becomes false once the customer adds to cart or orders.",
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation:
        "The email address of the customer who abandoned their browsing session.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation:
        "The phone number of the customer who abandoned their browsing session.",
    },
    browsedPageUrl: {
      dataType: "Maybe Url",
      documentation: "The URL of the page the customer was browsing.",
    },
    ...browsedProductsFields,
  },
});

export const baseMarketingNonShopifyCheckoutAbandonmentSchema = defineSchema({
  fields: {
    ...baseAbandonmentSchema.fields,
    isAbandonment: {
      dataType: "Boolean",
      documentation: "A flag to indicate that this is an abandonment.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    time: {
      dataType: "DateTime",
      documentation: "When the last customer action on checkout occurred.",
    },
    isCheckoutAbandoned: {
      dataType: "Boolean",
      label: "Checkout abandoned",
      documentation:
        "Whether the checkout is still abandoned. Becomes false once the customer places an order.",
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation:
        "The email address of the customer who abandoned their browsing session.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation:
        "The phone number of the customer who abandoned their browsing session.",
    },
    checkout: {
      dataType: "Maybe Shopify Checkout",
      documentation: "Data about the customer's checkout session.",
      hideFromConditionStep: true,
    },
    // TODO: booka66 - Remove this legacy cartSubtotal field once all legacy cart subtotal automations are migrated to use the cart condition
    cartSubtotal: {
      dataType: "Maybe Number",
      documentation:
        "The subtotal amount of the abandoned cart (in the store's currency).",
    },
    cartProductCount: {
      dataType: "Maybe Number",
      documentation: "The number of unique products in the abandoned cart.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    ...branchableCartContextFields(
      "Branch on what is in the abandoned cart: its products, collections, tags, count, or subtotal.",
    ),
    discountApplications: {
      dataType: "Maybe Discount Applications",
      documentation:
        "Discount codes and automatic discounts applied to the checkout.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    cartPreDiscountTotal: {
      dataType: "Maybe Number",
      documentation: "The cart total before any discounts were applied.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    storeUrl: {
      dataType: "Maybe Url",
      documentation: "The web address of your store.",
    },
    checkoutUrl: {
      dataType: "Maybe Url",
      documentation: "The URL of the checkout that the customer abandoned.",
    },
    productInCartName: {
      dataType: "Maybe Text",
      documentation: "The name of the first product in the abandoned checkout.",
    },
    ...cartProductsFields,
  },
});

export const exampleBaseMarketingNonShopifyCartAbandonmentSchemaInstance: SchemaInstance<
  typeof baseMarketingNonShopifyCartAbandonmentSchema
> = {
  ...exampleAbandonmentSchemaInstance,
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  isCartAbandoned: true,
  isAbandonment: true,
  time: Temporal.Now.instant(),
  cartContext: dummyCartContext(false),
  storeUrl: "https://example.com",
  checkoutUrl: "https://example.com/checkout",
  productInCartName: "Example Product",
  cartProducts: exampleTriggerProducts,
};

export const exampleBaseMarketingCsBrowseAbandonmentSchemaInstance: SchemaInstance<
  typeof baseMarketingCsBrowseAbandonmentSchema
> = {
  ...exampleAbandonmentSchemaInstance,
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  isBrowseAbandoned: true,
  isAbandonment: true,
  browsedProducts: exampleTriggerProducts,
  time: Temporal.Now.instant(),
  cartContext: dummyCartContext(false),
  browsedPageUrl: "https://example.com/collections/shoes",
};

export const exampleBaseMarketingNonShopifyCheckoutAbandonmentSchemaInstance: SchemaInstance<
  typeof baseMarketingNonShopifyCheckoutAbandonmentSchema
> = {
  ...exampleAbandonmentSchemaInstance,
  isAbandonment: true,
  time: Temporal.Now.instant(),
  isCheckoutAbandoned: true,
  cartProducts: exampleTriggerProducts,
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  checkout: {
    attributes: [],
    billingAddress: null,
    buyerAcceptsEmailMarketing: false,
    buyerAcceptsSmsMarketing: false,
    currencyCode: CurrencyCode.Usd,
    delivery: null,
    discountApplications: [],
    discountsAmount: null,
    email: "john.doe@example.com",
    lineItems: [],
    localization: {
      country: { isoCode: null },
      language: { isoCode: "en" },
      market: { handle: null, id: null },
    },
    order: null,
    shippingAddress: null,
    shippingLine: null,
    smsMarketingPhone: null,
    subtotalPrice: { amount: 100, currencyCode: CurrencyCode.Usd },
    phone: "+1234567890",
    token: "",
    totalPrice: { amount: 105, currencyCode: CurrencyCode.Usd },
    totalTax: { amount: 5, currencyCode: CurrencyCode.Usd },
    transactions: [],
  },
  cartSubtotal: 100,
  cartProductCount: 3,
  discountApplications: exampleDiscountApplications,
  cartPreDiscountTotal: 125,
  cartContext: dummyCartContext(false),
  storeUrl: "https://example.com",
  checkoutUrl: "https://example.com/checkout",
  productInCartName: "Example Product",
};

export const exampleBaseMarketingEmailSchemaInstance: SchemaInstance<
  typeof baseMarketingEmailSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  customerEmail: "john@fake.com",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  viewInBrowserLink: undefined, // gets populated later
  cartContext: dummyCartContext(true),
  customerFirstName: "John",
  customerLastName: "Doe",
  customerFullName: "John Doe",
};

export const exampleSegmentMembershipChangeSchemaInstance: SchemaInstance<
  typeof customerGroupMembershipChangeSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  customerEmail: "john@fake.com",
  customerPhone: "+1234567890",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  cartContext: dummyCartContext(true),
  segment: "65f9d2d37fd7c5a1d2c19876",
  customerFirstName: "John",
  customerLastName: "Doe",
  customerFullName: "John Doe",
};

export const baseMarketingSmsSchema = defineSchema({
  fields: {
    ...baseMarketingSchemaWithoutInteractiveCart.fields,
    customerPhoneNumber: {
      dataType: "Phone",
      documentation: "The phone number of the customer.",
    },
    customer: { dataType: "Customer", documentation: "The customer object." },
  },
});

const exampleBaseMarketingSignupSchema: SchemaInstance<
  typeof baseMarketingSignupSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  alreadySubscribed: false,
  entryPoint: MarketingEntryPoint.SIGNUP_FORM,
  cartContext: dummyCartContext(true),
  customerFirstName: "John",
  customerLastName: "Doe",
  customerFullName: "John Doe",
  form: "66f8d6d38fd4c4b4d1c19876",
  formId: "66f8d6d38fd4c4b4d1c19876",
  customerEventId: "67f8d6d38fd4c4b4d1c19876",
};

export const exampleEmailMarketingSignupSchema: SchemaInstance<
  typeof emailMarketingSignupSchema
> = {
  ...exampleBaseMarketingSignupSchema,
  discountCode: "EMAILABCD",
  customerEmail: "john@fake.com",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
};

export const smsMarketingSignupSchema = defineSchema({
  fields: {
    ...baseMarketingSignupSchema.fields,
    discountCode: {
      dataType: "Maybe Text",
      documentation: "The discount code to share with the customer.",
    },
    customerPhoneNumber: {
      dataType: "Phone",
      documentation:
        "The phone number of the customer who signed up for marketing SMS.",
    },
  },
});

export const exampleSmsMarketingSignupSchema: SchemaInstance<
  typeof smsMarketingSignupSchema
> = {
  ...exampleBaseMarketingSignupSchema,
  customerPhoneNumber: "+1234567890",
  discountCode: "SMSABCD",
};

export const exampleBaseMarketingBackInStockSchemaInstance: SchemaInstance<
  typeof baseMarketingBackInStockSchema
> = {
  ...exampleBaseMarketingSchemaInstance,
  productVariantId: "123",
  backInStockProductUrl: "https://example.com/products/example-product",
  backInStockProductTitle: "Example Product",
  unsubscribeLink: "https://example.com/unsubscribe",
  customerEmail: "john@fake.com",
  customerPhone: "+1234567890",
  oldInventoryCount: 0,
  newInventoryCount: 10,
  restockedProduct: exampleTriggerProduct,
};

export const exampleBaseMarketingLowInventorySchemaInstance: SchemaInstance<
  typeof baseMarketingLowInventorySchema
> = {
  ...exampleBaseMarketingSchemaInstance,
  productVariantId: "123",
  productTitle: "Wireless Headphones",
  variantTitle: "Black",
  inventoryCount: 3,
  customerEmail: "john@fake.com",
  customerPhone: "+1234567890",
  lowStockProduct: exampleTriggerProduct,
};

export const marketingDateSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    date: { dataType: "Date", documentation: "The date the event happened." },
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email address of the customer.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer.",
    },
  },
});

export const exampleMarketingDateSchemaInstance: SchemaInstance<
  typeof marketingDateSchema
> = {
  ...exampleBaseMarketingSchemaInstance,
  date: getPlainDateFromDateUtc(new Date()),
  customerEmail: "john@fake.com",
  customerPhone: "+1234567890",
};

export const marketingGiftCardBalanceSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email address of the customer.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer.",
    },
    largestGiftCardBalance: {
      dataType: "Number",
      documentation:
        "Balance of the customer's single largest gift card, not their total.",
      hideFromInsertionModal: true,
    },
    formattedLargestGiftCardBalance: {
      dataType: "Text",
      documentation:
        "The balance of the customer's largest gift card, such as $50.00.",
      hideFromConditionStep: true,
    },
    largestGiftCardLastCharacters: {
      dataType: "Text",
      documentation:
        "Last four characters of the largest-balance card, the one worth telling them to spend.",
      hideFromConditionStep: true,
    },
    totalGiftCardBalance: {
      dataType: "Number",
      documentation: "The customer's balance summed across every gift card.",
      hideFromInsertionModal: true,
    },
    formattedTotalGiftCardBalance: {
      dataType: "Text",
      documentation:
        "The customer's balance across every gift card, such as $75.00.",
      hideFromConditionStep: true,
    },
    giftCardCount: {
      dataType: "Number",
      documentation: "How many gift cards the customer holds a balance on.",
      hideFromInsertionModal: true,
    },
    giftCardCurrencyCode: {
      dataType: "Text",
      documentation: "The currency of these balances, such as USD.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    allGiftCardIds: {
      dataType: "Multiple Text",
      documentation:
        "Every gift card the customer holds a balance on, largest balance first.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    allGiftCardBalances: {
      dataType: "Multiple Number",
      documentation: "Every card's balance, largest first.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    allFormattedGiftCardBalances: {
      dataType: "Multiple Text",
      documentation: "Every card's balance, largest first, such as $50.00.",
      hideFromConditionStep: true,
    },
    allGiftCardLastCharacters: {
      dataType: "Multiple Text",
      documentation:
        "Last four characters of every card, largest balance first.",
      hideFromConditionStep: true,
    },
    isLikelyRefund: {
      dataType: "Boolean",
      label: "Likely refund",
      documentation:
        "Whether the balance looks like refund credit rather than a purchased gift card.",
      hideFromInsertionModal: true,
    },
    hasNote: {
      dataType: "Boolean",
      documentation: "Whether the gift card has a note attached.",
      hideFromInsertionModal: true,
    },
    daysHeld: {
      dataType: "Maybe Number",
      documentation:
        "How many days the customer has held their oldest balance.",
      hideFromInsertionModal: true,
    },
  },
});

export const exampleMarketingGiftCardBalanceSchemaInstance: SchemaInstance<
  typeof marketingGiftCardBalanceSchema
> = {
  ...exampleBaseMarketingSchemaInstance,
  customerEmail: "john@fake.com",
  customerPhone: "+1234567890",
  largestGiftCardBalance: 31.41,
  formattedLargestGiftCardBalance: "$31.41",
  largestGiftCardLastCharacters: "abcd",
  totalGiftCardBalance: 43.94,
  formattedTotalGiftCardBalance: "$43.94",
  giftCardCount: 2,
  giftCardCurrencyCode: "USD",
  allGiftCardIds: ["1234567890", "9876543210"],
  allGiftCardBalances: [31.41, 12.53],
  allFormattedGiftCardBalances: ["$31.41", "$12.53"],
  allGiftCardLastCharacters: ["abcd", "efgh"],
  isLikelyRefund: true,
  hasNote: false,
  daysHeld: 45,
};

export const baseMarketingPriceDropSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    numericVariantId: {
      dataType: "Text",
      documentation: "The product variant ID that had a price drop.",
    },
    productTitle: {
      dataType: "Text",
      documentation: "The title of the product that had a price drop.",
    },
    variantTitle: {
      dataType: "Text",
      documentation: "The title of the variant that had a price drop.",
    },
    formattedPreviousPrice: {
      dataType: "Text",
      documentation: "The price before the drop, such as $99.99.",
    },
    formattedCurrentPrice: {
      dataType: "Text",
      documentation: "The current price, such as $79.99.",
    },
    formattedSavings: {
      dataType: "Text",
      documentation: "How much the customer saves, such as $20.00.",
    },
    formattedPriceDropPercentage: {
      dataType: "Text",
      documentation: "The size of the price drop, such as 20%.",
    },
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email address of the customer.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer.",
    },
    unsubscribeLink: {
      ...unsubscribeLinkFields.unsubscribeLink,
      documentation: "A link to unsubscribe from price drop notifications.",
    },
    ...oneClickUnsubscribeLinkFields,
    ...discountedProductFields,
  },
});

export const marketingWarrantyRegistrationSchema = defineSchema({
  fields: {
    ...baseMarketingEmailSchema.fields,
    ...discountCodeSchema.fields,
    shopNowLink: {
      dataType: "Maybe Url",
      documentation: "The link to the product on the store.",
    },
    product: {
      dataType: "Product",
      documentation:
        "The product that the customer is registering a warranty for.",
    },
    teamName: {
      dataType: "Text",
      documentation:
        "The name of the team that the customer is registering a warranty for.",
    },
    teamEmail: {
      dataType: "Email",
      documentation:
        "The email of the team that the customer is registering a warranty for.",
    },
  },
});

export const exampleMarketingWarrantyRegistrationSchemaInstance: SchemaInstance<
  typeof marketingWarrantyRegistrationSchema
> = {
  ...exampleBaseMarketingSchemaInstance,
  // cspell:disable-next-line
  discountCode: "WARRANTYABCD",
  customerEmail: "john@fake.com",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  viewInBrowserLink: undefined, // gets populated later
  shopNowLink: "https://example.com/shop-now",
  teamName: "Team Name",
  teamEmail: "team@fake.com",
  product: {
    id: 123,
    title: "T-Shirt",
    variantTitle: "Red T-Shirt",
    image: { src: "https://example.com/image.jpg" },
    price: 100,
    priceSet: { presentment_money: { amount: 100, currency_code: "USD" } },
    isRedo: false,
    requiresShipping: true,
    tags: ["tag1", "tag2"],
    properties: { prop1: "value1", prop2: "value2" },
    sku: "123",
    green_return: false,
    vendor: "vendor",
    handle: "handle",
    compareAtPrice: "100",
  },
};

export const exampleBaseMarketingPriceDropSchemaInstance: SchemaInstance<
  typeof baseMarketingPriceDropSchema
> = {
  ...exampleBaseMarketingSchemaInstance,
  numericVariantId: "123456",
  productTitle: "Wireless Headphones",
  variantTitle: "Black",
  formattedPreviousPrice: "$99.99",
  formattedCurrentPrice: "$79.99",
  formattedSavings: "$20.00",
  formattedPriceDropPercentage: "20%",
  customerEmail: "john@fake.com",
  customerPhone: "+1234567890",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  discountedProduct: exampleTriggerProduct,
};

export const baseRefundReturnSubmittedSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    ...discountCodeSchema.fields,
    // Customer contact
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email address of the customer.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer.",
    },
    // Return context
    returnId: {
      dataType: "Text",
      documentation: "The ID of the submitted return.",
      hideFromInsertionModal: true,
    },
    returnNumber: {
      dataType: "Maybe Text",
      documentation: "The return number (external ID).",
    },
    refundAmount: {
      dataType: "Maybe Text",
      documentation: "The formatted refund amount (e.g., '$49.99').",
    },
    // Return status (refreshed after wait steps)
    returnStatus: {
      dataType: "Text",
      documentation:
        "Current status of the return (e.g., OPEN, COMPLETE, REJECTED). Refreshed after wait steps.",
    },
    returnIsProcessed: {
      dataType: "Boolean",
      documentation:
        "Whether the return has been processed (status is COMPLETE or REJECTED). Refreshed after wait steps.",
    },
    // Marker field for schema refresh mechanism. Always true here, so branching
    // on it is meaningless — hidden from both merchant-facing pickers.
    isRefundReturnSubmitted: {
      dataType: "Boolean",
      documentation: "Internal marker for schema refresh mechanism.",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
    },
    // Returned products
    returnedProductTitles: {
      dataType: "Multiple Text",
      documentation: "The titles of the products being returned.",
    },
    returnedProductCount: {
      dataType: "Number",
      documentation: "The number of products being returned.",
    },
    // Primary recommendation (for SMS/email preview)
    primaryRecommendationTitle: {
      dataType: "Maybe Text",
      documentation: "The title of the top recommended product.",
    },
    primaryRecommendationPrice: {
      dataType: "Maybe Text",
      documentation: "The formatted price of the top recommended product.",
    },
    primaryRecommendationReason: {
      dataType: "Maybe Text",
      documentation: "Why Redo recommended this product.",
    },
    primaryRecommendationIsSameProduct: {
      dataType: "Boolean",
      documentation:
        "Whether the top recommendation is a version of what the customer returned.",
    },
    // URLs
    storeUrl: {
      dataType: "Maybe Url",
      documentation: "The web address of your store.",
    },
    checkoutUrl: {
      dataType: "Maybe Url",
      documentation:
        "A checkout link that adds the recommended products to the cart.",
    },
    returnPortalUrl: {
      dataType: "Maybe Url",
      documentation: "URL to the customer's return status page.",
    },
    // Unsubscribe
    ...unsubscribeLinkFields,
    ...oneClickUnsubscribeLinkFields,
    // Timestamp
    time: {
      dataType: "DateTime",
      documentation: "When the return was submitted.",
    },
  },
});

export const exampleBaseRefundReturnSubmittedSchemaInstance: SchemaInstance<
  typeof baseRefundReturnSubmittedSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  discountCode: undefined,
  customerFirstName: "John",
  customerLastName: "Doe",
  customerFullName: "John Doe",
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  returnId: "ret_abc123",
  returnNumber: "RET-12345",
  refundAmount: "$49.99",
  returnStatus: "OPEN",
  returnIsProcessed: false,
  isRefundReturnSubmitted: true,
  returnedProductTitles: ["Classic T-Shirt", "Blue Jeans"],
  returnedProductCount: 2,
  primaryRecommendationTitle: "Premium Polo Shirt",
  primaryRecommendationPrice: "$59.99",
  primaryRecommendationReason:
    "Based on your style preferences, you might love this polo.",

  primaryRecommendationIsSameProduct: false,
  storeUrl: "https://example.com",
  checkoutUrl: "https://example.com/cart/123:1",
  returnPortalUrl: "https://example.com/returns/RET-12345",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  time: Temporal.Now.instant(),
  cartContext: {
    existingCartId: null,
    productIdsForRecommendations: [],
    alreadyInCart: [],
  },
};

export const baseExchangeProcessedWithCreditSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    ...discountCodeSchema.fields,
    // Customer contact
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email address of the customer.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer.",
    },
    // Return context
    returnId: {
      dataType: "Text",
      documentation: "The ID of the processed return.",
      hideFromInsertionModal: true,
    },
    returnNumber: {
      dataType: "Maybe Text",
      documentation: "The return number (external ID).",
    },
    storeCreditAmount: {
      dataType: "Text",
      documentation:
        "The formatted leftover store credit amount (e.g., '$25.00').",
    },
    // Exchange product info
    exchangeProductTitles: {
      dataType: "Multiple Text",
      documentation: "The titles of the products received in the exchange.",
    },
    exchangeProductCount: {
      dataType: "Number",
      documentation: "The number of products received in the exchange.",
    },
    // Abandoned cart / recommendation context
    hasAbandonedCart: {
      dataType: "Boolean",
      documentation:
        "Whether the customer has items in their abandoned cart. Use this to branch automation logic.",
    },
    // Primary recommendation (when no abandoned cart)
    primaryRecommendationTitle: {
      dataType: "Maybe Text",
      documentation:
        "The title of the top recommended product (used when no abandoned cart).",
    },
    primaryRecommendationPrice: {
      dataType: "Maybe Text",
      documentation:
        "The formatted price of the top recommended product (used when no abandoned cart).",
    },
    primaryRecommendationReason: {
      dataType: "Maybe Text",
      documentation:
        "Why Redo recommended this product, when there is no abandoned cart.",
    },
    // URLs
    storeUrl: {
      dataType: "Maybe Url",
      documentation: "The web address of your store.",
    },
    checkoutUrl: {
      dataType: "Maybe Url",
      documentation:
        "A checkout link holding either the abandoned cart items or recommended products.",
    },
    // Unsubscribe
    ...unsubscribeLinkFields,
    ...oneClickUnsubscribeLinkFields,
    // Timestamp
    time: {
      dataType: "DateTime",
      documentation: "When the return was processed.",
    },
  },
});

export const exampleBaseExchangeProcessedWithCreditSchemaInstance: SchemaInstance<
  typeof baseExchangeProcessedWithCreditSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  discountCode: undefined,
  customerFirstName: "John",
  customerLastName: "Doe",
  customerFullName: "John Doe",
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  returnId: "ret_abc123",
  returnNumber: "RET-12345",
  storeCreditAmount: "$25.00",
  exchangeProductTitles: ["Classic T-Shirt (Medium, Navy)"],
  exchangeProductCount: 1,
  hasAbandonedCart: false,
  primaryRecommendationTitle: "Premium Polo Shirt",
  primaryRecommendationPrice: "$59.99",
  primaryRecommendationReason:
    "Based on your recent exchange, you might like this polo shirt.",

  storeUrl: "https://example.com",
  checkoutUrl: "https://example.com/cart/123:1",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  time: Temporal.Now.instant(),
  cartContext: {
    existingCartId: null,
    productIdsForRecommendations: [],
    alreadyInCart: [],
  },
};

export const loyaltyLionEnrolledSchema = defineSchema({
  fields: {
    ...baseMarketingSchemaWithoutInteractiveCart.fields,
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email of the customer who enrolled in LoyaltyLion.",
      hideFromConditionStep: true,
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation:
        "The phone number of the customer who enrolled in LoyaltyLion.",
      hideFromConditionStep: true,
    },
    ...unsubscribeLinkFields,
    ...oneClickUnsubscribeLinkFields,
  },
});

export const exampleLoyaltyLionEnrolledSchemaInstance: SchemaInstance<
  typeof loyaltyLionEnrolledSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  customerFirstName: "John",
  customerLastName: "Doe",
  customerFullName: "John Doe",
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
};

export const loyaltyLionRewardAvailableSchema = defineSchema({
  fields: {
    ...baseMarketingSchemaWithoutInteractiveCart.fields,
    customerEmail: {
      dataType: "Maybe Email",
      documentation:
        "The email address of the customer who has a reward available.",
      hideFromConditionStep: true,
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation:
        "The phone number of the customer who has a reward available.",
      hideFromConditionStep: true,
    },
    ...unsubscribeLinkFields,
    ...oneClickUnsubscribeLinkFields,
    rewardId: {
      dataType: "Text",
      documentation:
        "The unique identifier of the available reward in LoyaltyLion.",
      hideFromConditionStep: true,
    },
    rewardTitle: {
      dataType: "Text",
      documentation:
        "The title/name of the available reward (e.g., '$10 off your next order').",
    },
    claimUrl: {
      dataType: "Maybe Url",
      documentation: "URL where the customer can view and claim their reward.",
    },
    pointCost: {
      dataType: "Number",
      documentation:
        "The number of loyalty points required to claim this reward.",
    },
    pointsApproved: {
      dataType: "Number",
      documentation:
        "The customer's available (approved) LoyaltyLion points balance when the reward became available. Excludes pending points.",
    },
    pointsPending: {
      dataType: "Number",
      documentation:
        "The customer's pending LoyaltyLion points balance when the reward became available. Pending points can't be spent yet.",
    },
  },
});

export const exampleLoyaltyLionRewardAvailableSchemaInstance: SchemaInstance<
  typeof loyaltyLionRewardAvailableSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  customerFirstName: "Sarah",
  customerLastName: "Johnson",
  customerFullName: "Sarah Johnson",
  customerEmail: "sarah.johnson@example.com",
  customerPhone: "+1234567890",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  rewardId: "ll_reward_12345",
  rewardTitle: "$10 off your next order",
  claimUrl: "https://example.com/loyalty/rewards/12345",
  pointCost: 500,
  pointsApproved: 520,
  pointsPending: 40,
};

export const loyaltyLionTierUpgradeSchema = defineSchema({
  fields: {
    ...baseMarketingSchemaWithoutInteractiveCart.fields,
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email of the customer who upgraded tiers.",
      hideFromConditionStep: true,
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer who upgraded tiers.",
      hideFromConditionStep: true,
    },
    ...unsubscribeLinkFields,
    ...oneClickUnsubscribeLinkFields,
    newTierName: {
      dataType: "Text",
      documentation: "The name of the tier the customer reached.",
    },
    previousTierName: {
      dataType: "Maybe Text",
      documentation: "The name of the tier the customer upgraded from.",
    },
  },
});

export const exampleLoyaltyLionTierUpgradeSchemaInstance: SchemaInstance<
  typeof loyaltyLionTierUpgradeSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  customerFirstName: "John",
  customerLastName: "Doe",
  customerFullName: "John Doe",
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  newTierName: "Gold",
  previousTierName: "Silver",
};

export const loyaltyLionApproachingTierUpgradeSchema = defineSchema({
  fields: {
    ...baseMarketingSchemaWithoutInteractiveCart.fields,
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email of the customer approaching a tier upgrade.",
      hideFromConditionStep: true,
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation:
        "The phone number of the customer approaching a tier upgrade.",
      hideFromConditionStep: true,
    },
    ...unsubscribeLinkFields,
    ...oneClickUnsubscribeLinkFields,
    nextTierName: {
      dataType: "Maybe Text",
      documentation: "The name of the next tier the customer is approaching.",
    },
    pointsNeeded: {
      dataType: "Maybe Number",
      documentation: "The number of points needed to reach the next tier.",
    },
  },
});

export const exampleLoyaltyLionApproachingTierUpgradeSchemaInstance: SchemaInstance<
  typeof loyaltyLionApproachingTierUpgradeSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  customerFirstName: "Jane",
  customerLastName: "Smith",
  customerFullName: "Jane Smith",
  customerEmail: "jane.smith@example.com",
  customerPhone: "+1234567890",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  nextTierName: "Platinum",
  pointsNeeded: 250,
};

export const loyaltyLionReferralCompleteSchema = defineSchema({
  fields: {
    ...baseMarketingSchemaWithoutInteractiveCart.fields,
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email of the customer who completed a referral.",
      hideFromConditionStep: true,
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation:
        "The phone number of the customer who completed a referral.",
      hideFromConditionStep: true,
    },
    ...unsubscribeLinkFields,
    ...oneClickUnsubscribeLinkFields,
  },
});

export const exampleLoyaltyLionReferralCompleteSchemaInstance: SchemaInstance<
  typeof loyaltyLionReferralCompleteSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  customerFirstName: "Alex",
  customerLastName: "Johnson",
  customerFullName: "Alex Johnson",
  customerEmail: "alex.johnson@example.com",
  customerPhone: "+1234567890",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
};

export const loyaltyLionRuleCompletedSchema = defineSchema({
  fields: {
    ...baseMarketingSchemaWithoutInteractiveCart.fields,
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email of the customer who completed a loyalty rule.",
      hideFromConditionStep: true,
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation:
        "The phone number of the customer who completed a loyalty rule.",
      hideFromConditionStep: true,
    },
    ...unsubscribeLinkFields,
    ...oneClickUnsubscribeLinkFields,
    ruleName: {
      dataType: "Text",
      documentation:
        'The name of the loyalty rule completed (e.g., "Refer a friend", "Happy Birthday").',
    },
  },
});

export const exampleLoyaltyLionRuleCompletedSchemaInstance: SchemaInstance<
  typeof loyaltyLionRuleCompletedSchema
> = {
  redoCustomerId: "65f9d2d37fd7c5a1d2c19876",
  customerFirstName: "Sam",
  customerLastName: "Williams",
  customerFullName: "Sam Williams",
  customerEmail: "sam.williams@example.com",
  customerPhone: "+1234567890",
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
  ruleName: "Happy Birthday",
};

export const baseMarketingProductSubscriptionCancelledSchema = defineSchema({
  fields: {
    ...baseMarketingSchema.fields,
    customerEmail: {
      dataType: "Maybe Email",
      documentation: "The email address of the customer.",
    },
    customerPhone: {
      dataType: "Maybe Phone",
      documentation: "The phone number of the customer.",
    },
    subscriptionId: {
      dataType: "Text",
      documentation:
        "The subscription platform's id for the cancelled subscription.",
      hideFromInsertionModal: true,
    },
    subscriptionPlatform: {
      dataType: "Enum",
      values: Object.values(SubscriptionIntegrationType),
      documentation:
        "Which subscription platform owned the cancelled subscription (recharge, skio, ...).",
    },
    productTitle: {
      dataType: "Maybe Text",
      documentation: "The title of the product the customer cancelled.",
    },
    variantTitle: {
      dataType: "Maybe Text",
      documentation: "The variant of the product the customer cancelled.",
    },
    cancellationReason: {
      dataType: "Maybe Text",
      documentation:
        "Vendor's cancellation reason string, if the customer or platform supplied one.",
    },
    cancellationReasonComments: {
      dataType: "Maybe Text",
      documentation:
        "Free-form cancellation comments the customer supplied alongside the reason.",
    },
    ...unsubscribeLinkFields,
    ...oneClickUnsubscribeLinkFields,
    ...subscriptionUpsellSchemaFields,
  },
});

export const exampleBaseMarketingProductSubscriptionCancelledSchemaInstance: SchemaInstance<
  typeof baseMarketingProductSubscriptionCancelledSchema
> = {
  ...exampleBaseMarketingSchemaInstance,
  customerEmail: "john.doe@example.com",
  customerPhone: "+1234567890",
  subscriptionId: "816575012",
  subscriptionPlatform: SubscriptionIntegrationType.RECHARGE,
  productTitle: "The Collection Snowboard: Oxygen",
  variantTitle: "",
  cancellationReason: "I no longer use this product",
  cancellationReasonComments: "Didn't mean to buy three lol",
  hasSubscribableProduct: true,
  subscribableProductInCartName: "The Collection Snowboard: Oxygen",
  subscriptionCheckoutUrl: "https://example.com/checkout/c/abcdef",
  subscribableLines: [
    {
      variantGid: "gid://shopify/ProductVariant/54287313568047",
      sellingPlanGid: "gid://shopify/SellingPlan/11029053743",
      quantity: 3,
    },
  ],
  unsubscribeLink: "https://example.com/unsubscribe",
  oneClickUnsubscribeLink: "https://example.com/one-click-unsubscribe",
};
